import * as Sentry from '@sentry/browser'
import Client from '../../client'
import config from '../../config'
import { defaultEbayRequirements } from '../products_v5/ProductFormContext'
import { Requirements, TemplateFormItem } from '../products_v5/components/JoinedForm/types'
import { getAutofilledSpecifics } from '../products_v5/components/JoinedForm/utils'
import { EbayForm } from './types'

const getAutofilledCondition = (
  features: { conditions?: { displayName: string; value: number }[] } | undefined,
  templateForm: TemplateFormItem[]
) => {
  let autofilledCondition = null

  templateForm?.forEach(templateFormValue => {
    const { label } = templateFormValue.props

    // features can be undefined before requirements finish loading (or after a failed load reset), so guard it.
    if (features?.conditions && label.toUpperCase() === 'Condition'.toUpperCase()) {
      autofilledCondition = features.conditions.find(c => {
        try {
          return c.displayName.toUpperCase() === (templateFormValue.props.value as string)?.toUpperCase()
        } catch (_e) {
          return false
        }
      })?.value
    }
  })

  return autofilledCondition
}

export type EbayRequirementsState = Requirements

export const emptyEbayRequirements = (): EbayRequirementsState => defaultEbayRequirements()

// Compute the specifics/condition that the current template values map onto. Pure + client-side, so it
// runs both after fetching requirements and again whenever template fields change (no API call).
export const computeEbayAutofill = (
  requirements: { specifics?: any[]; features?: any },
  templateForm?: TemplateFormItem[] | null
): Partial<EbayForm> => {
  const autofilled: Partial<EbayForm> = { specifics: {} }

  if (!config.EBAY_AUTOFILL_SPECIFICS() || !templateForm) {
    return autofilled
  }

  const autofilledSpecifics = getAutofilledSpecifics(requirements.specifics || [], templateForm)
  const autofilledCondition = getAutofilledCondition(requirements.features, templateForm)

  if (Object.keys(autofilledSpecifics).length > 0) {
    autofilled.specifics = autofilledSpecifics
  }
  if (autofilledCondition) {
    autofilled.condition = autofilledCondition
  }

  return autofilled
}

export const fetchEbayRequirements = async (
  categoryMarketCategoryId: number,
  templateForm?: TemplateFormItem[] | null
): Promise<{ requirements: EbayRequirementsState; autofilled: Partial<EbayForm> }> => {
  const requirementsResponse = await Promise.all([
    Client.getEbayCategoryFeatures(categoryMarketCategoryId),
    Client.categorySpecifics(categoryMarketCategoryId)
  ])

  const requirements = requirementsResponse.map(r => r && r.data).reduce((acc, curr) => Object.assign(acc, curr), {})

  const autofilled = computeEbayAutofill(requirements, templateForm)

  return {
    requirements: {
      loading: false,
      specifics: requirements.specifics || [],
      conditions: requirements.features?.conditions || [],
      bestOfferEnabled: requirements.features?.bestOfferEnabled,
      upc: requirements.features?.upc,
      ean: requirements.features?.ean,
      isbn: requirements.features?.isbn,
      features: requirements.features,
      itemConditionPolicies: requirements.itemConditionPolicies || []
    },
    autofilled
  }
}

export const loadEbayRequirements = async ({
  categoryMarketCategoryId,
  templateForm,
  onSuccess,
  onError
}: {
  categoryMarketCategoryId: number
  templateForm?: TemplateFormItem[] | null
  onSuccess: (result: { requirements: EbayRequirementsState; autofilled: Partial<EbayForm> }) => void
  onError: () => void
}) => {
  try {
    const result = await fetchEbayRequirements(categoryMarketCategoryId, templateForm)
    onSuccess(result)
  } catch (e) {
    Sentry.captureException(e)
    onError()
  }
}
